import {
    type RefObject, useEffect
} from 'react';

const controlSelector = 'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]';

// Use on a stable, programmatically focusable container whose controls can be
// replaced. A data-focus-scope keeps recovery within a row; data-focus-fallback
// identifies the preferred destination (for example, Cancel in a confirmation).
const useFocusRecovery = (containerRef: RefObject<HTMLElement | null>, enabled = true): void => {
    useEffect(() => {
        const container = containerRef.current;

        if (!enabled || !container) {
            return undefined;
        }

        let focused: HTMLElement | null = null;
        let scope: HTMLElement = container;
        let waitingForControls = false;
        const rememberFocus = (event: FocusEvent): void => {
            if (!(event.target instanceof HTMLElement) || !container.contains(event.target)) {
                focused = null;
                waitingForControls = false;

                return;
            }
            focused = event.target;
            if (focused !== container) {
                scope = focused.closest<HTMLElement>('[data-focus-scope]') ?? container;
            }
        };
        const observer = new MutationObserver(() => {
            if (!focused || (focused.isConnected && !focused.matches(':disabled') && !waitingForControls)) {
                return;
            }
            const active = document.activeElement;

            // Never take focus from another control or an overlaid dialog.
            if (active !== document.body && active !== container && active !== focused) {
                return;
            }
            const searchRoot = scope.isConnected ? scope : container;
            const candidates = [
                ...searchRoot.querySelectorAll<HTMLElement>('[data-focus-fallback]'),
                ...searchRoot.querySelectorAll<HTMLElement>(controlSelector)
            ];
            const destination = candidates.find((element) => (
                !element.matches(':disabled, [aria-disabled="true"]')
                && !element.closest('[hidden], [inert], [aria-hidden="true"]')
                && element.tabIndex >= 0
            ));

            if (destination) {
                waitingForControls = false;
                destination.focus();
            } else {
                waitingForControls = true;
                container.focus();
            }
        });

        document.addEventListener('focusin', rememberFocus);
        observer.observe(container, {
            attributeFilter: [
                'disabled'
            ],
            attributes: true,
            childList: true,
            subtree: true
        });

        return () => {
            document.removeEventListener('focusin', rememberFocus);
            observer.disconnect();
        };
    }, [
        containerRef,
        enabled
    ]);
};

export default useFocusRecovery;
